import {
  Component, DoCheck, Inject, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { ApprovalService } from 'src/app/services/approval.service';
import { CatalogSuggestionsService } from 'src/app/services/catalog-suggestions.service';
import { EntityService } from 'src/app/services/entity.service';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { ToastService } from 'src/app/services/toast.service';
import { ValidationService } from 'src/app/services/validation.service';
import { PendingFileInfo } from 'src/app/components/upload-file/upload-file.component';
import { validateCourseRequestGeneralInfo } from 'src/app/utils/course-validation.utils';
import { CatalogSuggestionAttachmentsComponent } from './components/catalog-suggestion-attachments/catalog-suggestion-attachments.component';

@Component({
  selector: 'app-update-my-catalog-suggestion',
  templateUrl: './update-my-catalog-suggestion.component.html',
  styleUrls: ['./update-my-catalog-suggestion.component.scss'],
})
export class UpdateMyCatalogSuggestionComponent implements OnInit, DoCheck, OnDestroy {
  @ViewChild(CatalogSuggestionAttachmentsComponent, { static: false })
  private attachmentsStep?: CatalogSuggestionAttachmentsComponent;

  course: any = {
    name: '',
    approvalId: '',   // this should be L0_NONE
    validationId: '', // this should be HONOR
    creditHours: '',
    courseType: '',
    instructor: '',
    cost: '',
    webUrl: '',
    description: '',
    async: false,
    virtual: false,
    anytime: false,
    location: '',
    roomNumber: '',
    courseDate: '',
    startTime: '',
    endTime: '',
  }

  // keep 3 steps (no extra step)
  steps = [
    { completed: false, text: 'General Info' },
    { completed: false, text: 'Attachments' },
    { completed: false, text: 'Review & finish' },
  ];

  selectedStep = this.steps[0];

  // IDs used by the Attachments step / uploader
  courseItemId: number | null = null;
  courseId: number | null = null;

  entity: any;
  approvalsSubscription: any;
  entitySubscription: any;
  validationSubscription: any;
  courseTypes: any;
  catalogSuggestionSub: any;
  originalFormattedCourse: any;

  noApprovalNeededId: number;

  private prevAnytime = false;
  private prevAsync = false;
  private prevVirtual = false;

  // Track validation state from child components
  generalInfoValidation: { isValid: boolean } = { isValid: false };

  constructor(
    public dialogRef: MatDialogRef<UpdateMyCatalogSuggestionComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private approvalService: ApprovalService,
    private entityService: EntityService,
    private catalogSuggestionsService: CatalogSuggestionsService,
    private toastService: ToastService,
    private loadingSpinnerService: LoadingSpinnerService,
    private validationService: ValidationService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.entityService.getEntity('CourseTypes');
    this.entitySubscription = this.entityService.entityData$.subscribe((entityData: any) => {
      this.entity = entityData;
      const activeCourseTypes = entityData?.CourseTypes?.filter((courseType: any) => courseType.activeFlag);
      this.courseTypes = activeCourseTypes;
    });

    this.approvalsSubscription = this.approvalService.approvalData$.subscribe((data: any) => {
      if (!data) return;
      const noApprovalNeeded = data.find((approval: any) => approval.kalpaIdentifier === 'L0_NONE');
      this.course.approvalId = Number(noApprovalNeeded?.id);
    });

    this.validationSubscription = this.validationService.validationTypes$.subscribe((data: any) => {
      if (!data) return;
      const honorValidation = data.find((validation: any) => validation.kalpaIdentifier === 'HONOR');
      this.course.validationId = Number(honorValidation?.id);
    });

    this.approvalService.getApprovalLevels();
    this.validationService.getValidationTypes();

    this.setIdsFromDialogData();
  }

  get pendingDocuments(): PendingFileInfo[] {
    return this.attachmentsStep?.getPendingDocuments?.() ?? [];
  }

  private setIdsFromDialogData = (): void => {
    const d = this.data;
    if (!d) return;

    const course = d?.course ?? d;

    const courseItems =
      course?.CourseItems ??
      course?.courseItems ??
      d?.CourseItems ??
      d?.courseItems ??
      null;

    const courseItem = Array.isArray(courseItems) ? (courseItems[0] ?? null) : courseItems;

    const itemId = Number(courseItem?.id ?? course?.courseItemId ?? course?.course_item_id ?? d?.courseItemId ?? d?.course_item_id);
    const cId = Number(courseItem?.courseId ?? course?.courseId ?? course?.course_id ?? course?.id ?? d?.courseId ?? d?.course_id);

    this.courseItemId = !isNaN(itemId) && itemId > 0 ? itemId : null;
    this.courseId = !isNaN(cId) && cId > 0 ? cId : null;
  }

  private normalizeOfferingDates = (): void => {
    if (this.course?.async) {
      this.course.courseDate = '';
      this.course.startTime = '';
      this.course.endTime = '';
      return;
    }

    if (this.course?.anytime) {
      this.course.startTime = '';
      this.course.endTime = '';
    }

    if (this.course?.virtual) {
      this.course.roomNumber = '';
    }
  }

  ngDoCheck(): void {
    const anytimeToggledOn = this.course?.anytime && !this.prevAnytime;
    const asyncToggledOn = this.course?.async && !this.prevAsync;
    const virtualToggledOn = this.course?.virtual && !this.prevVirtual;

    this.prevAnytime = !!this.course?.anytime;
    this.prevAsync = !!this.course?.async;
    this.prevVirtual = !!this.course?.virtual;

    if (anytimeToggledOn || asyncToggledOn || virtualToggledOn) {
      this.normalizeOfferingDates();
    }
  }

  closeDialog = () => {
    this.dialogRef.close();
  }

  private generalInfoFallbackValid = (): boolean => {
    return validateCourseRequestGeneralInfo(this.course).isValid;
  }

  nextDisabledCheck = () => {
    if (this.selectedStep === this.steps[0]) {
      // Prefer emitted validation, but fall back to a simple field check
      if (this.generalInfoValidation?.isValid === true) return false;
      return !this.generalInfoFallbackValid();
    }
    return false;
  }

  onGeneralInfoValidationChange = (validation: any) => {
    // Accept either: boolean OR { isValid: boolean }
    if (typeof validation === 'boolean') {
      this.generalInfoValidation = { isValid: validation };
      return;
    }
    if (validation && typeof validation?.isValid === 'boolean') {
      this.generalInfoValidation = { isValid: validation.isValid };
      return;
    }
    // last-resort coercion
    this.generalInfoValidation = { isValid: !!validation };
  }

  stepForward = () => {
    if (this.nextDisabledCheck()) return;

    this.selectedStep.completed = true;
    this.selectedStep = this.steps[this.steps.indexOf(this.selectedStep) + 1];
  }

  stepBack = () => {
    const prevIndex = this.steps.indexOf(this.selectedStep) - 1;
    if (prevIndex >= 0) {
      this.selectedStep = this.steps[prevIndex];
    }
  }

submitCatalogSuggestion = () => {
  this.normalizeOfferingDates();

  if (this.course.async) {
    delete this.course.courseDate;
  }

  Object.keys(this.course).forEach((key) => {
    (this.course[key] === null || this.course[key] === '') && delete this.course[key];
  });

  this.loadingSpinnerService.setIsLoading(true);

  this.catalogSuggestionSub = this.catalogSuggestionsService.submitCatalogSuggestion(this.course).subscribe({
    next: async (response: any) => {
      try {
        let courseItemId: number | null = null;

        // Check for courseItemIds array at data level (catalog suggestions format)
        if (response?.data?.courseItemIds) {
          if (Array.isArray(response.data.courseItemIds) && response.data.courseItemIds.length > 0) {
            courseItemId = Number(response.data.courseItemIds[0]);
          }
        }

        // Check for items array at data level (catalog suggestions format)
        if ((!courseItemId || isNaN(courseItemId)) && response?.data?.items) {
          if (Array.isArray(response.data.items) && response.data.items.length > 0) {
            courseItemId = Number(response.data.items[0].id);
          } else if (typeof response.data.items === 'object') {
            courseItemId = Number(response.data.items.id);
          }
        }

        // Check for CourseItems (course requests format - capital C)
        if ((!courseItemId || isNaN(courseItemId)) && response?.data?.course?.CourseItems) {
          if (Array.isArray(response.data.course.CourseItems) && response.data.course.CourseItems.length > 0) {
            courseItemId = Number(response.data.course.CourseItems[0].id);
          } else if (typeof response.data.course.CourseItems === 'object') {
            courseItemId = Number(response.data.course.CourseItems.id);
          }
        }

        if ((!courseItemId || isNaN(courseItemId)) && response?.data?.CourseItems) {
          if (Array.isArray(response.data.CourseItems) && response.data.CourseItems.length > 0) {
            courseItemId = Number(response.data.CourseItems[0].id);
          } else if (typeof response.data.CourseItems === 'object') {
            courseItemId = Number(response.data.CourseItems.id);
          }
        }

        if ((!courseItemId || isNaN(courseItemId)) && (response?.data?.courseItemId || response?.data?.course_item_id)) {
          courseItemId = Number(response.data.courseItemId ?? response.data.course_item_id);
        }

        // Keep IDs in sync so the Attachments step can render consistently
        this.courseItemId = courseItemId && !isNaN(courseItemId) && courseItemId > 0 ? courseItemId : null;
        this.courseId = Number(response?.data?.course?.id ?? response?.data?.courseId ?? response?.data?.course_id ?? this.courseId) || this.courseId;

        const hasPendingDocuments = (this.attachmentsStep?.getPendingDocuments?.()?.length ?? 0) > 0;

        if (hasPendingDocuments && courseItemId && !isNaN(courseItemId) && courseItemId > 0) {
          try {
            this.attachmentsStep?.setCourseIdsFromData?.(response.data?.course || response.data || response);
            await this.attachmentsStep?.uploadFilesForCourse?.(courseItemId);
          } catch (err) {
            // attachment upload failed - continue anyway
          } finally {
            this.loadingSpinnerService.setIsLoading(false);
            this.dialogRef.close(response);
          }
          return;
        }

        this.loadingSpinnerService.setIsLoading(false);
        this.dialogRef.close(response);
      } catch (err) {
        this.loadingSpinnerService.setIsLoading(false);
        this.dialogRef.close(response);
      }
    },
    error: (error) => {
      this.loadingSpinnerService.setIsLoading(false);
      this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Oops, something went wrong',
          text: error?.error?.errMsg || error?.error?.pgErrorInfo?.message || error?.error?.message || 'Something went wrong. Please try again.',
        },
      });
    },
  });
}

  ngOnDestroy(): void {
    this.entitySubscription?.unsubscribe();
    this.catalogSuggestionSub?.unsubscribe();
  }
}
